import { BasicValidationRules } from 'common-utils/interfaces';
import { InputSelectorResult, PdsAbstractInputWidget } from './pds-abstract-input-widget';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { RadioButtonOption } from './interfaces/radio-button-option';

import { pdsLogger } from './pds-logger';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-radio-button-widget.ts');

export class PdsRadioButtonWidget extends PdsAbstractInputWidget {
  is = 'pds-radio-button-widget';

  //RadioButtonWidget Properties
  dataShowClear: string;
  options: RadioButtonOption[] | null = null;

  //AbstractInputWidget Properties
  properties = {
    name: String, //We construct the id using the name property
    dataLabelText: String,
    dataShowLabel: {
      type: String,
      value: null
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataTooltipText: String,
    dataUseAlternateColorScheme: {
      type: String,
      value: 'false'
    },
    disabled: {
      type: String,
      observer: 'updateDisabled',
      value: 'false'
    },
    dataIsfieldonly: {
      //This is for adding a class to get rid of the bottom padding.
      type: String,
      value: 'false'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    //Option to activate the validation indicator
    dataUseValidIndicator: {
      type: String,
      value: 'false'
    },
    dataCustomvalidate: String,
    //Option to clear a radio button group of a previously selected value
    dataShowClear: {
      type: String,
      value: 'false'
    },
    options: {
      type: Array,
      observer: 'renderInputs'
    }
  };

  //RadioButtonWidget Local Properties
  private _modelValue: string | undefined = undefined;

  /**
   * Generates the necessary validation rules for the radio button widget.
   */
  generateValidationRules(): void {
    let validationRules: BasicValidationRules = pdsValidationRulesEngine.getBasicValidationRules();
    validationRules.isText = true;
    validationRules.setProperty('isRequired', this.dataIsrequired, Boolean);

    this.validators = pdsValidationRulesEngine.getValidatorsForRules(validationRules);
    if ('dataCustomvalidate' in this) {
      this.validators.push({
        execute: this.pds.validation.getCustomValidate(this.dataCustomvalidate),
        condition: {
          element: this
        }
      });
    }
  }

  /**
   * Gets the checked input element. Defaults to using query selector to find the first checked child input.
   */
  inputSelector(): InputSelectorResult {
    let input = this.querySelector('input:checked');
    return {
      value: input && input.value || '',
      modelValue: this._modelValue
    };
  }

  /**
   * Force the options list to repopulate.
   */
  redrawOptions(): void {
    log(`Redrawing the options for ${this.name}.`);

    this.redraw('options');

    // set the value back to the model value after the options have been rebuilt
    this.setValue(this.modelValue, true);
  }

  /**
   * @param modelValue
   * @param skip
   * @returns returns if the model value was actually set.
   */
  setModelValue(modelValue: string, skip = false): boolean {
    if (modelValue !== this._modelValue) {
      log(`Setting ${this.name}'s model value from '${this._modelValue}' to '${modelValue}'.`);
      this._modelValue = modelValue;

      if (skip !== true) {
        this.setValue(modelValue, true);
      }

      return true;
    }

    return false;
  }

  /**
   * @param value - The radio button value the user selected.
   * @param skip - skips setting the model value. (Keeps from getting into recursive loops)
   */
  setValue(value: string | undefined, skip = false): void {
    if (value == null) {
      value = '';
    }

    let radioInputElements = this.querySelectorAll('input');
    if (radioInputElements != null) {
      for (let i = 0; i < radioInputElements.length; i++) {
        radioInputElements[i].checked = (radioInputElements[i].value === value);
      }
    }

    if (skip !== true) {
      this.setModelValue(value, true);
    }
  }

  clearRadioButton(): void {
    this.setModelValue('');
    this.inputChange();
  }

  /**
   * Creates the DOM manually so the options can be redrawn no matter what sorts of changes
   * happen to the underlying objects.
   */
  renderInputs(): void {
    let container: HTMLElement = this.querySelector('ul');
    container.innerHTML = ''; //blow away the old stuff.
    let optionIndex = 0;
    if (this.options != null) {
      for (let option of this.options) {
        //Create the elements.
        let liEl = createElement('li');
        let labelEl = createElement('label');
        let inputEl = <HTMLInputElement>createElement('input');
        let divEl = createElement('div');
        let pEl = createElement('p');

        //Add Attributes and hook up listeners to elements.
        labelEl.setAttribute('for', `${this.name}-option-${optionIndex}`);

        inputEl.setAttribute('id', `${this.name}-option-${optionIndex}`);
        inputEl.setAttribute('type', 'radio');
        inputEl.setAttribute('name', this.name);
        inputEl.setAttribute('value', option.value);
        inputEl.addEventListener('blur', (event) => {
          this.focusLost(event);
        });
        inputEl.addEventListener('change', (event) => {
          this.inputChange();
        });
        inputEl.required = (this.dataIsrequired === 'true');
        inputEl.disabled = (this.isDisabled() === 'disabled');
        inputEl.checked = (this._modelValue === option.value);

        divEl.classList.add('pds-label-text');
        divEl.textContent = option.text;

        pEl.classList.add('pds-help-text');
        pEl.textContent = option.helperText || null;

        //Hook up the elements
        container.appendChild(liEl);
        liEl.appendChild(labelEl);
        liEl.appendChild(pEl);
        labelEl.appendChild(inputEl);
        labelEl.appendChild(divEl);
        optionIndex++;
      }
    }
  }

  updateDisabled(): void {
    let radioInputElements = this.querySelectorAll('input');
    if (radioInputElements != null) {
      for (let i = 0; i < radioInputElements.length; i++) {
        radioInputElements[i].disabled = (this.isDisabled() === 'disabled');
      }
    }
  }

  //Overriding pds-abstract-input-widget inputChange method to fix on-blur issues on Mac
  inputChange(): void {
    this.errorPresentWhenBlurOccurred = true;
    super.inputChange();
  }

  //Overriding pds-abstract-input-widget validateWidget method to fix on-blur issues on Mac
  validateWidget(showValidationResult = false): boolean {
    return super.validateWidget(true);
  }
}

function createElement(tagName: string): HTMLElement {
  let element: HTMLElement = document.createElement(tagName);
  element.classList.add('pds-radio-button-widget');
  return element;
}
